import { useState, type FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db73kc0d6mcdj80oo7a0";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const team = [
  { name: "选片编辑组", role: "内容策划", desc: "负责片库整理与专题推荐，保证内容更新及时、分类清晰。" },
  { name: "产品体验组", role: "设计与交互", desc: "打磨浏览、筛选与搜索体验，让找片更快更顺手。" },
  { name: "技术支持组", role: "平台维护", desc: "保障站点稳定运行，持续优化页面性能与移动端适配。" },
];

export default function About() {
  useDocumentTitle(
    "关于我们 - 光棍影院",
    "了解光棍影院的定位、团队与联系方式。我们专注提供清晰易用的电影与电视剧内容浏览体验，欢迎通过反馈表单与我们联系。"
  );

  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      setSubmitted(true);
      form.reset();
      return;
    }

    formData.delete("website_alt");
    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, String(value)));

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg);

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam ? serverMsg : "提交失败，请稍后重试或直接发送邮件给我们。"
        );
        return;
      }

      setStatus("success");
      setSubmitted(true);
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1">
        <section className="w-full bg-foreground-950 pt-28 pb-14 md:pt-32 md:pb-16 px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold text-background-50 font-heading">
              关于光棍影院
            </h1>
            <p className="mt-4 text-sm md:text-base text-background-300 leading-relaxed">
              我们是一个专注影视内容浏览的门户站点，希望通过清晰的分类、快速的搜索与多维度的筛选，让每一位影迷都能更轻松地找到想看的电影与电视剧。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading flex items-center gap-2 mb-4">
                <i className="ri-compass-3-line text-primary-600"></i>
                网站定位
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                光棍影院面向喜爱追剧看片的普通观众，提供最新电影与电视剧的剧情介绍、元信息与相关推荐。我们不追求花哨，而是把「找得到、看得懂、选得快」放在第一位——无论是想随手看看有什么新片，还是按类型、地区、年份精准筛选，都能轻松完成。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-4">
                本站所有影视信息、评分与图片均为演示示例内容，播放功能暂未接入真实接口，仅用于展示产品体验。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading flex items-center gap-2 mb-4">
                <i className="ri-service-line text-accent-500"></i>
                服务宗旨
              </h2>
              <ul className="flex flex-col gap-4">
                {[
                  { icon: "ri-flashlight-line", title: "快速查找", desc: "搜索与筛选双通道，几秒锁定目标影片。" },
                  { icon: "ri-layout-grid-line", title: "清晰分类", desc: "电影、电视剧分区明确，类型题材一目了然。" },
                  { icon: "ri-heart-3-line", title: "贴心体验", desc: "本地收藏与相关推荐，追片更省心。" },
                ].map((f) => (
                  <li key={f.title} className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-md bg-accent-100 flex items-center justify-center shrink-0">
                      <i className={`${f.icon} text-xl text-accent-700`}></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{f.title}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">{f.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading flex items-center gap-2 mb-6">
            <i className="ri-team-line text-primary-600"></i>
            团队介绍
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {team.map((m) => (
              <div key={m.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-user-3-line text-2xl text-primary-700"></i>
                </span>
                <h3 className="text-base font-bold text-foreground-950">{m.name}</h3>
                <p className="text-xs font-medium text-accent-700 mt-1">{m.role}</p>
                <p className="text-sm text-foreground-600 mt-3 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading flex items-center gap-2 mb-4">
                <i className="ri-contacts-line text-primary-600"></i>
                联系方式
              </h2>
              <ul className="flex flex-col gap-4 text-sm">
                <li className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-md bg-secondary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-xl text-secondary-700"></i>
                  </span>
                  <span className="text-foreground-700">反馈邮箱：support@ggfilm.example.com</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-md bg-secondary-100 flex items-center justify-center">
                    <i className="ri-time-line text-xl text-secondary-700"></i>
                  </span>
                  <span className="text-foreground-700">服务时间：每日 09:00 - 22:00</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-md bg-secondary-100 flex items-center justify-center">
                    <i className="ri-map-pin-line text-xl text-secondary-700"></i>
                  </span>
                  <span className="text-foreground-700">内容均为线上演示，暂无线下服务点</span>
                </li>
              </ul>
              <p className="mt-6 text-xs text-foreground-500 leading-relaxed">
                温馨提示：本表单为演示用途，提交内容不会用于商业推广。如需反馈内容错误或功能建议，欢迎留言。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="text-xl font-bold text-foreground-950 font-heading mb-5">
                给我们留言
              </h2>

              {submitted ? (
                <div className="rounded-lg bg-accent-50 border border-accent-200 p-6 text-center">
                  <span className="inline-flex w-14 h-14 rounded-full bg-accent-500 items-center justify-center mb-3">
                    <i className="ri-check-line text-3xl text-foreground-950"></i>
                  </span>
                  <p className="text-base font-bold text-foreground-950">感谢反馈！</p>
                  <p className="text-sm text-foreground-600 mt-1">
                    我们已收到你的留言，会尽快查看并不断改进。
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setStatus("idle");
                    }}
                    className="mt-4 text-sm font-medium text-primary-600 hover:text-accent-600 whitespace-nowrap"
                  >
                    再写一条
                  </button>
                </div>
              ) : (
                <form
                  id="contact-feedback-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label htmlFor="cf-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      称呼
                    </label>
                    <input
                      id="cf-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className="w-full rounded-md px-4 py-2.5 bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="cf-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="w-full rounded-md px-4 py-2.5 bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-type" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      反馈类型
                    </label>
                    <select
                      id="cf-type"
                      name="type"
                      defaultValue="功能建议"
                      className="w-full rounded-md px-4 py-2.5 bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                    >
                      <option>功能建议</option>
                      <option>内容纠错</option>
                      <option>合作咨询</option>
                      <option>其他问题</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="cf-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="cf-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="请描述你的建议或问题（最多 500 字）"
                      className="w-full rounded-md px-4 py-2.5 bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-guard"
                  />

                  {status === "error" && (
                    <p className="text-sm text-red-600">{formError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap"
                  >
                    {status === "loading" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                        提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-lg"></i>
                        提交反馈
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}